import * as React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import * as _ from 'lodash';
import classnames from 'classnames';
import { connect } from 'react-redux';
import * as tokenService from '@nexxt/common/firebase/auth/userDetails';
import { getTenantLevel } from '@nexxt/common/firebase/auth/userDetails';
import {
    HealthProblem,
    HealthProblemType,
    PROJECT_STATUS,
    QuestionSummary
} from '@nexxt/common/types';
import { INCA_HELP_URLS } from '@nexxt/common/constants';
import { isNumber } from 'lodash';
import {
    UilApps,
    UilChannel,
    UilFolderOpen,
    UilCommentAltLines,
    UilQuestionCircle,
    UilUserCircle,
    UilFileAlt,
    UilUsersAlt,
    UilCommentQuestion,
    UilPresentation,
    UilShoppingCart,
    UilAngleLeftB,
    UilAngleRightB,
    UilImagePlus,
    UilBox,
    UilFolderQuestion,
    UilUser,
    UilHouseUser,
    UilSignout,
    UilCommentAltChartLines
} from '@iconscout/react-unicons';
import { push } from 'connected-react-router';
import {
    WORKSPACE,
    MEDIA_LIBRARY,
    PROJECTS,
    QUESTION_LIBRARY,
    USERS,
    MANAGE_PROFILE,
    MANAGE_ACCOUNTS,
    PLAN_AND_BILLING,
    OPTION_PACKS,
    AI_CODING,
    LIBRARIES,
    EDIT_NEW_PROJECT,
    AUDIENCE,
    QUESTIONS,
    PREVIEW,
    CHECKOUT
} from '../../../constants/routes';
import { dataTestid } from '../../../constants/dataTestid';
import AuthContext from '../../../contexts/AuthContext';
import { interpolate } from '../../../utils/string';
import { logoutRequest } from '../../../actions/authentication.actions';
import NotifyHOC from '../../../HOC/Notifications';
import * as Messages from '../../../mapping/messages';
import HealthIcon from '../designElements/HealthIcon';
import { sortHealthProblemByTab } from '../../../utils/project';
import { TENANT_LEVEL } from '@nexxt/common/types';
import config from '../../../config';

// Sidebar updates
import { SideBar as IncaSideBar, SideBarItem } from 'inca-ui';
import { STATIC_STORAGE_URL } from '@nexxt/common/constants';
import { loadSession, saveSession } from '../../../services/session.service';
import { PortalSession } from '../../../constants/project';

interface SidebarProps {
    viewMode: string;
    location: any;
    isSidebarExpand: boolean;
    isFeedbackOpen: boolean;
    toastNotification: any;
    projectHealth: HealthProblem[];
    currentProject: any;
    questionSummaries: QuestionSummary[];
    isMobile: boolean;
    openFeedback: () => void;
    sidebarToggle: (toggleOn: boolean) => void;
    changePath: (path: string) => void;
    logoutRequest: () => void;
    clearProjects: () => void;
    toggleShowNotSupported: (toggle: boolean) => void;
    getInitialExpandedStateFromStorage: () => { [id: number]: boolean };
}
const Sidebar: React.FC<SidebarProps> = (props: SidebarProps) => {
    const [hasProblemInQuestion, setHasProblemInQuestion] =
        React.useState(null);
    const [hasProblemInAudience, setHasProblemInAudience] =
        React.useState(null);
    const [hasProblemInOverview, setHasProblemInOverview] =
        React.useState(null);
    const [projectHealth, setProjectHealth] = React.useState(
        props.projectHealth || []
    );

    const { firebaseAuth } = React.useContext(AuthContext);
    const tenantLevel = getTenantLevel();
    const helpDeskUrl =
        tenantLevel in INCA_HELP_URLS
            ? INCA_HELP_URLS[tenantLevel]
            : INCA_HELP_URLS['DEFAULT'];

    React.useEffect(() => {
        const hasProblem = sortHealthProblemByTab(props.projectHealth);
        setProjectHealth(props.projectHealth);
        setHasProblemInAudience(hasProblem.audience);
        setHasProblemInQuestion(hasProblem.question);
        setHasProblemInOverview(hasProblem.overview);
    }, [props.projectHealth]);

    const signOut = async () => {
        try {
            await save();
            await axios.post(
                `${process.env.INCA_SERVER_URL}/auth/signout`,
                {},
                { withCredentials: true }
            );
            await firebaseAuth.signOut();
            props.toastNotification.success(Messages.LOGOUT_MESSAGE);
            localStorage.clear();
            window.location.href = process.env.AUTH_URL;
        } catch (e) {
            props.toastNotification.error(Messages.LOGOUT_MESSAGE_ERROR);
            console.log(e);
        }
    };
    const save = async () => {
        try {
            const sidebarState = props.getInitialExpandedStateFromStorage();
            const { data } = await loadSession();
            if (!sidebarState) {
                saveSession({
                    ...data,
                    lastSidebarExpanded: true
                });
            } else {
                saveSession({
                    ...data,
                    lastSidebarExpanded: sidebarState[user.id]
                });
            }
        } catch (err) {
            console.error('No session found', err);
        }
    };

    const user = tokenService.getUserDetails();
    const pathChunks = props.location.pathname.split('/');
    const queryString = props.viewMode ? `?mode=${props.viewMode}` : '';

    const disabledLink = user.passwordExpired;
    const [activeLink, setActiveLink] = React.useState(props.location.pathname);

    React.useEffect(() => {
        setActiveLink(props.location.pathname);
    }, [props.location.pathname]);

    const renderSwitch = (param) => {
        switch (true) {
            case [
                MANAGE_PROFILE,
                USERS,
                MANAGE_ACCOUNTS,
                PLAN_AND_BILLING
            ].includes(param):
                return (
                    <React.Fragment>
                        <SideBarItem
                            link={MANAGE_PROFILE}
                            disabled={disabledLink}
                        >
                            <Link
                                exact={true}
                                to={MANAGE_PROFILE}
                                onClick={() => {
                                    setActiveLink(MANAGE_PROFILE);
                                    if (props.isMobile) {
                                        props.sidebarToggle(false);
                                        props.toggleShowNotSupported(true);
                                    }
                                }}
                            >
                                <div className="sidebar-item-content">
                                    <UilUser size={20} />
                                    {props.isSidebarExpand && (
                                        <span className="sidebar-item-text">
                                            Manage Profile
                                        </span>
                                    )}
                                </div>
                            </Link>
                        </SideBarItem>
                        <SideBarItem
                            link={MANAGE_ACCOUNTS}
                            disabled={disabledLink}
                        >
                            <Link
                                to={MANAGE_ACCOUNTS}
                                onClick={() => {
                                    setActiveLink(MANAGE_ACCOUNTS);
                                    if (props.isMobile) {
                                        props.sidebarToggle(false);
                                        props.toggleShowNotSupported(true);
                                    }
                                }}
                            >
                                <div className="sidebar-item-content">
                                    <UilHouseUser size={20} />
                                    {props.isSidebarExpand && (
                                        <span className="sidebar-item-text">
                                            User Management
                                        </span>
                                    )}
                                </div>
                            </Link>
                        </SideBarItem>
                        <SideBarItem disabled={disabledLink}>
                            <Link onClick={signOut}>
                                <div className="sidebar-item-content">
                                    <UilSignout size={20} />
                                    {props.isSidebarExpand && (
                                        <span className="sidebar-item-text">
                                            Logout
                                        </span>
                                    )}
                                </div>
                            </Link>
                        </SideBarItem>
                    </React.Fragment>
                );
            // case param.includes(LIBRARIES):
            // Hide libraries tab for now
            //     return (
            //         <React.Fragment>
            //             <SideBarItem link={MEDIA_LIBRARY}>
            //                 <Link
            //                     exact={true}
            //                     to={MEDIA_LIBRARY}
            //                     onClick={() => {
            //                         props.sidebarToggle(false);
            //                         setActiveLink(MEDIA_LIBRARY);
            //                         props.isMobile &&
            //                             props.toggleShowNotSupported(true);
            //                     }}
            //                 >
            //                     <div className="sidebar-item-content">
            //                         <UilImagePlus size={20} />
            //                         {props.isSidebarExpand && (
            //                             <span className="sidebar-item-text">
            //                                 Media Library
            //                             </span>
            //                         )}
            //                     </div>
            //                 </Link>
            //             </SideBarItem>
            //             <SideBarItem link={OPTION_PACKS}>
            //                 <Link
            //                     exact={true}
            //                     to={OPTION_PACKS}
            //                     onClick={() => {
            //                         props.sidebarToggle(false);
            //                         setActiveLink(OPTION_PACKS);
            //                         props.isMobile &&
            //                             props.toggleShowNotSupported(true);
            //                     }}
            //                 >
            //                     <div className="sidebar-item-content">
            //                         <UilBox size={20} />
            //                         {props.isSidebarExpand && (
            //                             <span className="sidebar-item-text">
            //                                 Option Pack Library
            //                             </span>
            //                         )}
            //                     </div>
            //                 </Link>
            //             </SideBarItem>
            //             <SideBarItem link={QUESTION_LIBRARY} disabled={true}>
            //                 <Link
            //                     exact={true}
            //                     to={QUESTION_LIBRARY}
            //                     onClick={() => {
            //                         props.sidebarToggle(false);
            //                         setActiveLink(QUESTION_LIBRARY);
            //                         props.isMobile &&
            //                             props.toggleShowNotSupported(true);
            //                     }}
            //                 >
            //                     <div className="sidebar-item-content">
            //                         <UilFolderQuestion size={20} />
            //                         {props.isSidebarExpand && (
            //                             <span className="sidebar-item-text">
            //                                 Question Library
            //                             </span>
            //                         )}
            //                     </div>
            //                 </Link>
            //             </SideBarItem>
            //         </React.Fragment>
            //     );
            //     break;
            case param.includes(PROJECTS) &&
                pathChunks.filter((x) => x).length >= 2 &&
                tenantLevel !== TENANT_LEVEL.INCA_WIZARD:
                const projectId = pathChunks[2];
                const questionsMenuHealthType =
                    projectId === 'new' || !props.questionSummaries.length
                        ? HealthProblemType.DRAFT
                        : hasProblemInQuestion || HealthProblemType.SUCCESS;
                const audienceMenuHealthType =
                    projectId === 'new'
                        ? HealthProblemType.DRAFT
                        : hasProblemInAudience || HealthProblemType.SUCCESS;
                const overviewMenuHealthType =
                    projectId === 'new'
                        ? HealthProblemType.DRAFT
                        : hasProblemInOverview || HealthProblemType.SUCCESS;
                const projectMenuHealthType =
                    projectId === 'new'
                        ? HealthProblemType.DRAFT
                        : HealthProblemType.SUCCESS;
                const projectLaunchHealthType =
                    projectId === 'new'
                        ? HealthProblemType.DRAFT
                        : hasProblemInQuestion ||
                          hasProblemInAudience ||
                          hasProblemInOverview ||
                          HealthProblemType.SUCCESS;
                const generateLink = (projectRoute) =>
                    `${interpolate(projectRoute, { projectId })}${queryString}`;
                const onLaunchModal = _.last(pathChunks) === 'launch';
                const disableProjectSidebarItem =
                    (projectId === 'new' ? true : false) || onLaunchModal;
                return (
                    <React.Fragment>
                        <SideBarItem
                            link={generateLink(EDIT_NEW_PROJECT).split('?')[0]}
                            disabled={disableProjectSidebarItem}
                        >
                            <Link
                                exact={true}
                                to={generateLink(EDIT_NEW_PROJECT)}
                                onClick={() => {
                                    setActiveLink(
                                        generateLink(
                                            EDIT_NEW_PROJECT.split('?')[0]
                                        )
                                    );
                                    if (props.isMobile) {
                                        props.sidebarToggle(false);
                                        props.toggleShowNotSupported(true);
                                    }
                                }}
                            >
                                <div className="sidebar-item-content">
                                    <UilFileAlt size={20} />
                                    {!props.isSidebarExpand ? (
                                        !props.isMobile && (
                                            <div className="sidebar-item-content-icon">
                                                <HealthIcon
                                                    active={true}
                                                    type={
                                                        overviewMenuHealthType
                                                    }
                                                    className={`sidebar-item--health ${overviewMenuHealthType.toLowerCase()}`}
                                                    questionID="sidebar--overview"
                                                    iconSize="12"
                                                />
                                            </div>
                                        )
                                    ) : (
                                        <div className="sidebar-item-content-icon">
                                            <HealthIcon
                                                active={true}
                                                type={overviewMenuHealthType}
                                                className={`sidebar-item--health ${overviewMenuHealthType.toLowerCase()}`}
                                                questionID="sidebar--overview"
                                                iconSize="12"
                                            />
                                        </div>
                                    )}

                                    {props.isSidebarExpand && (
                                        <span className="sidebar-item-text">
                                            Overview
                                        </span>
                                    )}
                                </div>
                            </Link>
                        </SideBarItem>
                        <SideBarItem
                            link={generateLink(AUDIENCE).split('?')[0]}
                            disabled={disableProjectSidebarItem}
                        >
                            <Link
                                to={generateLink(AUDIENCE)}
                                onClick={() => {
                                    setActiveLink(
                                        generateLink(AUDIENCE).split('?')[0]
                                    );
                                    if (props.isMobile) {
                                        props.sidebarToggle(false);
                                        props.toggleShowNotSupported(true);
                                    }
                                }}
                            >
                                <div
                                    className="sidebar-item-content"
                                    data-test={dataTestid.SIDEBAR_AUDIENCE}
                                >
                                    <UilUsersAlt size={20} />
                                    {!props.isSidebarExpand ? (
                                        !props.isMobile && (
                                            <div className="sidebar-item-content-icon">
                                                <HealthIcon
                                                    active={true}
                                                    type={
                                                        audienceMenuHealthType
                                                    }
                                                    className={`sidebar-item--health ${audienceMenuHealthType?.toLowerCase()}`}
                                                    questionID="sidebar--overview"
                                                    iconSize="12"
                                                />
                                            </div>
                                        )
                                    ) : (
                                        <div className="sidebar-item-content-icon">
                                            <HealthIcon
                                                active={true}
                                                type={audienceMenuHealthType}
                                                className={`sidebar-item--health ${audienceMenuHealthType?.toLowerCase()}`}
                                                questionID="sidebar--audience"
                                                iconSize="12"
                                            />
                                        </div>
                                    )}
                                    {props.isSidebarExpand && (
                                        <span className="sidebar-item-text">
                                            Audience
                                        </span>
                                    )}
                                </div>
                            </Link>
                        </SideBarItem>
                        <SideBarItem
                            link={generateLink(QUESTIONS).split('?')[0]}
                            disabled={disableProjectSidebarItem}
                        >
                            <Link
                                to={generateLink(QUESTIONS)}
                                onClick={() => {
                                    setActiveLink(
                                        generateLink(QUESTIONS).split('?')[0]
                                    );
                                    if (props.isMobile) {
                                        props.sidebarToggle(false);
                                        props.toggleShowNotSupported(true);
                                    }
                                }}
                            >
                                <div
                                    className="sidebar-item-content"
                                    data-test={dataTestid.SIDEBAR_QUESTION}
                                >
                                    <UilCommentQuestion size={20} />
                                    {!props.isSidebarExpand ? (
                                        !props.isMobile && (
                                            <div className="sidebar-item-content-icon">
                                                <HealthIcon
                                                    active={true}
                                                    type={
                                                        questionsMenuHealthType
                                                    }
                                                    className={`sidebar-item--health ${questionsMenuHealthType?.toLowerCase()}`}
                                                    questionID="sidebar--overview"
                                                    iconSize="12"
                                                />
                                            </div>
                                        )
                                    ) : (
                                        <div className="sidebar-item-content-icon">
                                            <HealthIcon
                                                active={true}
                                                type={questionsMenuHealthType}
                                                className={`sidebar-item--health ${questionsMenuHealthType?.toLowerCase()}`}
                                                questionID="sidebar--questions"
                                                iconSize="12"
                                            />
                                        </div>
                                    )}
                                    {props.isSidebarExpand && (
                                        <span className="sidebar-item-text">
                                            Question Builder
                                        </span>
                                    )}
                                </div>
                            </Link>
                        </SideBarItem>
                        <SideBarItem
                            link={generateLink(PREVIEW).split('?')[0]}
                            disabled={disableProjectSidebarItem}
                        >
                            <Link
                                to={generateLink(PREVIEW)}
                                onClick={() => {
                                    setActiveLink(
                                        generateLink(PREVIEW).split('?')[0]
                                    );
                                    if (props.isMobile) {
                                        props.sidebarToggle(false);
                                        props.toggleShowNotSupported(true);
                                    }
                                }}
                            >
                                <div className="sidebar-item-content">
                                    <UilPresentation size={20} />
                                    {!props.isSidebarExpand ? (
                                        !props.isMobile && (
                                            <div className="sidebar-item-content-icon">
                                                <HealthIcon
                                                    active={true}
                                                    type={projectMenuHealthType}
                                                    className={`sidebar-item--health ${projectMenuHealthType}`}
                                                    questionID="sidebar--overview"
                                                    iconSize="12"
                                                />
                                            </div>
                                        )
                                    ) : (
                                        <div className="sidebar-item-content-icon">
                                            <HealthIcon
                                                active={true}
                                                type={projectMenuHealthType}
                                                className={`sidebar-item--health ${projectMenuHealthType}`}
                                                questionID="sidebar--overview"
                                                iconSize="12"
                                            />
                                        </div>
                                    )}
                                    {props.isSidebarExpand && (
                                        <span className="sidebar-item-text">
                                            Preview
                                        </span>
                                    )}
                                </div>
                            </Link>
                        </SideBarItem>
                        <SideBarItem
                            link={generateLink(CHECKOUT).split('?')[0]}
                            disabled={disableProjectSidebarItem}
                        >
                            <Link
                                to={generateLink(CHECKOUT)}
                                onClick={() => {
                                    setActiveLink(
                                        generateLink(CHECKOUT).split('?')[0]
                                    );
                                    if (props.isMobile) {
                                        props.sidebarToggle(false);
                                        props.toggleShowNotSupported(true);
                                    }
                                }}
                            >
                                <div className="sidebar-item-content">
                                    <UilShoppingCart size={20} />
                                    {!props.isSidebarExpand ? (
                                        !props.isMobile && (
                                            <div className="sidebar-item-content-icon">
                                                <HealthIcon
                                                    active={true}
                                                    type={
                                                        projectLaunchHealthType
                                                    }
                                                    className={`sidebar-item--health ${projectLaunchHealthType}`}
                                                    questionID="sidebar--overview"
                                                    iconSize="12"
                                                />
                                            </div>
                                        )
                                    ) : (
                                        <div className="sidebar-item-content-icon">
                                            <HealthIcon
                                                active={true}
                                                type={projectLaunchHealthType}
                                                className={`sidebar-item--health ${projectLaunchHealthType}`}
                                                questionID="sidebar--overview"
                                                iconSize="12"
                                            />
                                        </div>
                                    )}
                                    {props.isSidebarExpand && (
                                        <span className="sidebar-item-text">
                                            {props.currentProject.status &&
                                            props.currentProject.status !==
                                                PROJECT_STATUS.DRAFT
                                                ? 'Republish'
                                                : 'Checkout'}
                                        </span>
                                    )}
                                </div>
                            </Link>
                        </SideBarItem>
                    </React.Fragment>
                );
            default:
                return (
                    <React.Fragment>
                        <SideBarItem link={PROJECTS}>
                            <Link
                                exact={true}
                                to={PROJECTS}
                                onClick={() => {
                                    setActiveLink(PROJECTS);
                                    if (props.isMobile) {
                                        props.sidebarToggle(false);
                                        props.toggleShowNotSupported(true);
                                    }
                                }}
                            >
                                <div className="sidebar-item-content">
                                    <UilCommentAltChartLines size={20} />
                                    {props.isSidebarExpand && (
                                        <span className="sidebar-item-text">
                                            Project Directory
                                        </span>
                                    )}
                                </div>
                            </Link>
                        </SideBarItem>
                        {![
                            TENANT_LEVEL.INCA_WIZARD,
                            TENANT_LEVEL.SANDBOX
                        ].includes(tenantLevel) ? (
                            <SideBarItem link={AI_CODING}>
                                <Link
                                    exact={true}
                                    to={AI_CODING}
                                    onClick={() => {
                                        setActiveLink(AI_CODING);
                                        if (props.isMobile) {
                                            props.sidebarToggle(false);
                                            props.toggleShowNotSupported(true);
                                        }
                                    }}
                                >
                                    <div className="sidebar-item-content">
                                        <UilChannel size={20} />
                                        {props.isSidebarExpand && (
                                            <span className="sidebar-item-text">
                                                AI Coding Directory
                                            </span>
                                        )}
                                    </div>
                                </Link>
                            </SideBarItem>
                        ) : (
                            <></>
                        )}
                        <SideBarItem
                            link={MANAGE_ACCOUNTS}
                            disabled={disabledLink}
                        >
                            <Link
                                to={MANAGE_ACCOUNTS}
                                onClick={() => {
                                    setActiveLink(MANAGE_ACCOUNTS);
                                    if (props.isMobile) {
                                        props.sidebarToggle(false);
                                        props.toggleShowNotSupported(true);
                                    }
                                }}
                            >
                                <div className="sidebar-item-content">
                                    <UilHouseUser size={20} />
                                    {props.isSidebarExpand && (
                                        <span className="sidebar-item-text">
                                            User Management
                                        </span>
                                    )}
                                </div>
                            </Link>
                        </SideBarItem>
                        {/* <SideBarItem link={MEDIA_LIBRARY}>
                            <Link
                                exact={true}
                                to={MEDIA_LIBRARY}
                                onClick={() => {
                                    props.sidebarToggle(false);
                                    setActiveLink(MEDIA_LIBRARY);
                                    props.isMobile &&
                                        props.toggleShowNotSupported(true);
                                }}
                            >
                                <div className="sidebar-item-content">
                                    <UilFolderOpen size={20} />
                                    {props.isSidebarExpand && (
                                        <span className="sidebar-item-text">
                                            Libraries
                                        </span>
                                    )}
                                </div>
                            </Link>
                        </SideBarItem> */}
                    </React.Fragment>
                );
        }
    };

    const handleLogo = () => {
        if (!props.isMobile) {
            return (
                <SideBarItem logo>
                    <Link
                        exact={true}
                        to={WORKSPACE}
                        onClick={() => {
                            if (props.isMobile) {
                                props.sidebarToggle(false);
                                props.toggleShowNotSupported(true);
                            }
                        }}
                    >
                        {props.isSidebarExpand ? (
                            <div className="sidebar-logo">
                                <svg width="40" height="40">
                                    <image
                                        id="nexxt-bot-logo-4"
                                        width="40"
                                        height="40"
                                        href={`${STATIC_STORAGE_URL}/img/inca_blue_favicon.png`}
                                    />
                                </svg>
                                <svg width="100" height="40">
                                    <image
                                        id="nexxt-bot-logo-4"
                                        width="100"
                                        height="40"
                                        href={`${STATIC_STORAGE_URL}/img/inca_logo_text.png`}
                                    />
                                </svg>
                            </div>
                        ) : (
                            <svg width="30" height="30">
                                <image
                                    id="nexxt-bot-logo-4"
                                    width="30"
                                    height="30"
                                    href={`${STATIC_STORAGE_URL}/img/inca_blue_favicon.png`}
                                />
                            </svg>
                        )}
                    </Link>
                </SideBarItem>
            );
        } else {
            return (
                <SideBarItem logo>
                    <Link
                        exact={true}
                        to={WORKSPACE}
                        onClick={() => {
                            if (props.isMobile) {
                                props.sidebarToggle(false);
                                props.toggleShowNotSupported(true);
                            }
                        }}
                    >
                        {props.isSidebarExpand && (
                            <div className="sidebar-logo">
                                <svg
                                    width="30"
                                    height="30"
                                    style={{
                                        marginTop: '2px'
                                    }}
                                >
                                    <image
                                        id="nexxt-bot-logo-4"
                                        width="30"
                                        height="30"
                                        href={`${STATIC_STORAGE_URL}/img/inca_blue_favicon.png`}
                                    />
                                </svg>
                                <svg width="100" height="35">
                                    <image
                                        id="nexxt-bot-logo-4"
                                        width="80"
                                        height="35"
                                        href={`${STATIC_STORAGE_URL}/img/inca_logo_text.png`}
                                    />
                                </svg>
                            </div>
                        )}
                    </Link>
                </SideBarItem>
            );
        }
    };

    return (
        <IncaSideBar
            isExpanded={props.isSidebarExpand}
            sidebarToggle={props.sidebarToggle}
            selectedLink={activeLink}
            footerProps={{
                openFeedback: props.openFeedback,
                helpDeskUrl: helpDeskUrl
            }}
            logo={[
                `${STATIC_STORAGE_URL}/img/inca_blue_favicon.png`,
                `${STATIC_STORAGE_URL}/img/inca_logo_text.png`
            ]}
            isMobile={props.isMobile}
        >
            {handleLogo()}
            <SideBarItem link={WORKSPACE} disabled={disabledLink}>
                <Link
                    exact={true}
                    to={WORKSPACE}
                    onClick={() => {
                        setActiveLink(WORKSPACE);
                        if (props.isMobile) {
                            props.sidebarToggle(false);
                            props.toggleShowNotSupported(true);
                        }
                    }}
                >
                    <div
                        className="sidebar-footer-item"
                        data-test={dataTestid.SIDEBAR_HOME}
                    >
                        <UilApps size={20} />
                        {props.isSidebarExpand && (
                            <span className="sidebar-item-text">Home</span>
                        )}
                    </div>
                </Link>
            </SideBarItem>
            {renderSwitch(props.location.pathname)}

            <SideBarItem footer link={MANAGE_PROFILE}>
                <Link
                    exact={true}
                    to={MANAGE_PROFILE}
                    onClick={() => {
                        setActiveLink(MANAGE_PROFILE);
                        if (props.isMobile) {
                            props.sidebarToggle(false);
                            props.toggleShowNotSupported(true);
                        }
                    }}
                >
                    <div className="sidebar-footer-item">
                        {user?.profilePicture ? (
                            <div className="sidebar-profile-picture">
                                <img src={user?.profilePicture} />
                            </div>
                        ) : (
                            <UilUserCircle size={20} />
                        )}
                        {props.isSidebarExpand && (
                            <span className="sidebar-item-text">
                                {user?.firstName}
                            </span>
                        )}
                    </div>
                </Link>
            </SideBarItem>
        </IncaSideBar>
    );
};

const mapDispatchToProps = (dispatch) => ({
    logoutRequest: () => dispatch(logoutRequest()),
    changePath: (path) => {
        dispatch(push(path));
    }
});

const mapStateToProps = (store) => {
    const {
        router: { location },
        projectReducer: { projectHealth, currentProject },
        questionReducer: { questionSummaries },
        utilsReducer: { viewMode }
    } = store;
    return {
        location,
        projectHealth,
        currentProject,
        questionSummaries: questionSummaries[currentProject.id] || [],
        viewMode
    };
};

export default NotifyHOC(connect(mapStateToProps, mapDispatchToProps)(Sidebar));
